import React from 'react'
import { LinkActionConfig, TreeNavigatorDataSchema } from './types'

type UseTreeNavigatorGraphArgs = {
  dataSchema: TreeNavigatorDataSchema
  linkAction?: LinkActionConfig
}

export function useTreeNavigatorGraph({ dataSchema, linkAction }: UseTreeNavigatorGraphArgs) {
  const nodesById = React.useMemo(() => {
    const map = new Map<number, (typeof dataSchema.data.nodes)[number]>()
    for (const node of dataSchema.data.nodes) {
      map.set(node.id, node)
    }
    return map
  }, [dataSchema])

  const incomingCount = React.useMemo(() => {
    const count = new Map<number, number>()
    for (const node of dataSchema.data.nodes) {
      count.set(node.id, 0)
    }
    for (const link of dataSchema.data.links) {
      count.set(link.targetNodeId, (count.get(link.targetNodeId) ?? 0) + 1)
    }
    return count
  }, [dataSchema])

  const roots = React.useMemo(
    () => dataSchema.data.nodes.filter((node) => (incomingCount.get(node.id) ?? 0) === 0),
    [dataSchema, incomingCount]
  )

  const childrenMap = React.useMemo(() => {
    const map = new Map<number, number[]>()
    for (const link of dataSchema.data.links) {
      const children = map.get(link.sourceNodeId) ?? []
      children.push(link.targetNodeId)
      map.set(link.sourceNodeId, children)
    }
    return map
  }, [dataSchema])

  const linkValueByEdge = React.useMemo(() => {
    const map = new Map<string, number>()
    for (const link of dataSchema.data.links) {
      map.set(`${link.sourceNodeId}->${link.targetNodeId}`, link.percentageVal)
    }
    return map
  }, [dataSchema])

  const linkMarkerPositionByEdge = React.useMemo(() => {
    const map = new Map<string, number>()
    for (const link of dataSchema.data.links) {
      map.set(`${link.sourceNodeId}->${link.targetNodeId}`, link.markerPosition ?? 0)
    }
    return map
  }, [dataSchema])

  const linkFilterDataByEdge = React.useMemo(() => {
    const map = new Map<string, Record<string, unknown>>()
    for (const link of dataSchema.data.links) {
      const edgeKey = `${link.sourceNodeId}->${link.targetNodeId}`
      linkAction?.filterMapping?.forEach((mapping) => {
        map.set(edgeKey, { ...(map.get(edgeKey) ?? {}), [mapping.filterId]: link[mapping.dataKey] })
      })
    }
    return map
  }, [dataSchema, linkAction])

  return {
    roots,
    nodesById,
    childrenMap,
    linkValueByEdge,
    linkMarkerPositionByEdge,
    linkFilterDataByEdge,
  }
}
